import React, { useCallback, useState } from 'react';
import { Box, Button, Dialog, IconButton, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { enqueueSnackbar } from 'notistack';
import {
    useDossierActionContext,
    useCurrentDossierCtxDispatch,
    REFRESH_CURRENT_DOSSIER_CTX,
} from '../../../providers';
import { ModalPaper } from '../ExportDossierModal/ModalPaper';
import SelectGreffeContent from './SelectGreffeContent';
import { GreffeChoice } from './GreffeCard';

export type SelectGreffeModalProps = {
    greffeChoices?: number[];
    onClose: () => void;
    prestaId: number[];
};
const SelectGreffeModal: React.FunctionComponent<SelectGreffeModalProps> = function ({
    greffeChoices = [],
    prestaId,
    onClose,
}) {
    const { palette } = useTheme();
    const [currentChoices, setCurrentChoices] = useState<GreffeChoice[]>(
        greffeChoices.map((id) => ({ id, greffes: '' })),
    );
    const [submitting, setSubmitting] = useState<boolean>(false);
    const dispatch = useCurrentDossierCtxDispatch();
    const { confirmCurrentAction } = useDossierActionContext();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);
    const { prestationFormaliteApi } = useFormaliteService();

    const handleModalClose = () => {
        onClose();
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await Promise.all(
                prestaId.map((id) =>
                    prestationFormaliteApi.apiPrestationsIdGreffesPut({
                        id: String(id),
                        apiPrestationsIdGreffesPutRequest: {
                            greffes: currentChoices.map((choice) => choice.id),
                        },
                    }),
                ),
            );
            enqueueSnackbar('Le dossier a bien été sauvegardé');
            confirmCurrentAction();
            refreshDossier();
            onClose();
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du greffe', {
                variant: 'error',
            });
            setSubmitting(false);
        }
    };

    const cancelBtnSx = {
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Dialog
            open
            PaperComponent={ModalPaper}
        >
            <IconButton
                aria-label="close"
                onClick={handleModalClose}
                sx={{
                    fontSize: '16px',
                    position: 'absolute',
                    right: '20px',
                    top: '30px',
                    color: palette.grey[500],
                }}
            >
                <FaOptionIcon icon={['fas', 'close']} />
            </IconButton>
            <Box sx={{ overflowY: 'auto', maxHeight: '60vh' }}>
                <SelectGreffeContent
                    onSelectGreffe={(greffes: Array<GreffeChoice>) => setCurrentChoices(greffes)}
                    currentChoices={currentChoices}
                />
            </Box>
            <Box
                sx={{
                    marginTop: '1em',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0 1em',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleModalClose}
                    sx={cancelBtnSx}
                    disabled={submitting}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    Valider
                </Button>
            </Box>
        </Dialog>
    );
};

export default SelectGreffeModal;
